/* ==========================================================================
   intl-phone — shared behaviour for the international phone field.
   Wraps intl-tel-input v29 (loaded separately as intlTelInputWithUtils).

   Each phone <input> carries:
     data-intl-phone               marks it for init
     data-intl-phone-for="<id>"    id of the hidden <input> that submits E.164
     data-intl-phone-required      (optional) field is required
     data-intl-phone-auto          (optional) self-manage error UI + submit block

   Public API (window.IntlPhone):
     initAll()            (re)scan and init every [data-intl-phone]
     init(input)          init a single input
     validate(input)      -> { valid: bool, message: string }
     getNumber(input)     -> E.164 string ('' when empty)
     sync(input)          copy the current E.164 number into the hidden input
   ========================================================================== */
(function () {
  'use strict';

  function lib() {
    return window.intlTelInput;
  }

  function hiddenFor(input) {
    var id = input.getAttribute('data-intl-phone-for');
    return id ? document.getElementById(id) : null;
  }

  function fieldOf(input) {
    return input.closest('[data-intl-phone-field]');
  }

  function messageFor(iti) {
    var L = lib();
    if (!L) return 'Please enter a valid phone number.';
    switch (iti.getValidationError()) {
      case L.VALIDATION_ERROR.TOO_SHORT:
        return 'This phone number is too short.';
      case L.VALIDATION_ERROR.TOO_LONG:
        return 'This phone number is too long.';
      case L.VALIDATION_ERROR.INVALID_COUNTRY_CODE:
        return 'Invalid country code.';
      default:
        return 'Please enter a valid phone number.';
    }
  }

  function getNumber(input) {
    var iti = input.itiInstance;
    var raw = input.value.trim();
    if (!iti) return raw;
    var L = lib();
    var num = L ? iti.getNumber(L.NUMBER_FORMAT.E164) : '';
    if (!num && raw) {
      // Fallback if utils failed to load: dial code + raw digits.
      var c = iti.getSelectedCountry();
      var digits = input.value.replace(/\D/g, '');
      num = c && c.dialCode ? '+' + c.dialCode + digits : raw;
    }
    return num || '';
  }

  function syncHidden(input) {
    var hidden = hiddenFor(input);
    if (!hidden) return;
    hidden.value = input.value.trim() ? getNumber(input) : '';
  }

  function validate(input) {
    var iti = input.itiInstance;
    var raw = input.value.trim();
    var required = input.hasAttribute('data-intl-phone-required');

    if (!raw) {
      return { valid: !required, message: required ? 'Enter your phone number.' : '' };
    }
    // === false only: if utils aren't ready isValidNumber() returns null —
    // don't punish the user for our own loading hiccup.
    if (iti && iti.isValidNumber() === false) {
      return { valid: false, message: messageFor(iti) };
    }
    return { valid: true, message: '' };
  }

  /* ----- auto-mode UI (self-managed error display) ----- */
  function showError(input, message) {
    var field = fieldOf(input);
    if (field) field.classList.add('is-invalid');
    var box = field && field.querySelector('[data-intl-phone-error]');
    if (box) {
      box.hidden = false;
      var txt = box.querySelector('[data-intl-phone-error-text]') || box;
      txt.textContent = message;
    }
  }

  function clearError(input) {
    var field = fieldOf(input);
    if (field) field.classList.remove('is-invalid');
    var box = field && field.querySelector('[data-intl-phone-error]');
    if (box) box.hidden = true;
  }

  function initInput(input) {
    var L = lib();
    if (!L || !input || input.itiInstance) return;

    var iti = L(input, {
      initialCountry: 'us',
      separateDialCode: true,
      strictMode: true,
      formatAsYouType: true,
      countrySearch: true,
      countrySelectorMode: 'DROPDOWN',
      countryOrder: ['us', 'ca', 'gb', 'au'],
      placeholderNumberPolicy: 'POLITE'
    });
    input.itiInstance = iti;

    var auto = input.hasAttribute('data-intl-phone-auto');

    input.addEventListener('input', function () {
      if (auto) clearError(input);
      syncHidden(input);
    });
    input.addEventListener('countrychange', function () {
      if (auto) clearError(input);
      syncHidden(input);
    });
    input.addEventListener('blur', function () {
      syncHidden(input);
      if (auto) {
        var r = validate(input);
        if (r.valid) clearError(input);
        else showError(input, r.message);
      }
    });

    var form = input.closest('form');
    if (form) {
      if (auto && !form.dataset.intlPhoneAuto) {
        // Capture phase so we run before any framework submit handler and can
        // stop an invalid submit outright.
        form.dataset.intlPhoneAuto = '1';
        form.addEventListener(
          'submit',
          function (e) {
            syncHidden(input);
            var r = validate(input);
            if (!r.valid) {
              e.preventDefault();
              e.stopImmediatePropagation();
              showError(input, r.message);
              input.focus();
            }
          },
          true
        );
      } else if (!auto && !form.dataset.intlPhoneSync) {
        // Non-auto host owns validation; we only guarantee the hidden E.164
        // value is fresh before its own submit handler reads it.
        form.dataset.intlPhoneSync = '1';
        form.addEventListener(
          'submit',
          function () {
            var fields = form.querySelectorAll('[data-intl-phone]');
            Array.prototype.forEach.call(fields, syncHidden);
          },
          true
        );
      }
    }

    syncHidden(input);
  }

  function initAll() {
    if (!lib()) return;
    var inputs = document.querySelectorAll('[data-intl-phone]');
    Array.prototype.forEach.call(inputs, initInput);
  }

  window.IntlPhone = {
    initAll: initAll,
    init: initInput,
    validate: validate,
    getNumber: getNumber,
    sync: syncHidden
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  // Theme editor: re-init when a section is (re)rendered.
  document.addEventListener('shopify:section:load', initAll);
})();
